// App.tsx
import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Sessions from "./pages/Sessions";
import SessionDetail from "./pages/SessionDetail";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import Login from "./pages/Login";
import {
  Menu,
  BarChart2,
  Settings as SettingsIcon,
  Calendar,
  CreditCard,
} from "lucide-react";
import { authClient } from "./auth/auth-client";

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_MAX_ATTEMPTS = 3;
const RESEND_WINDOW_MS = 15 * 60 * 1000;
const RESEND_STORAGE_KEY = "rr_verify_resend_meta";

type ResendMeta = {
  lastSentAt: number;
  windowStartAt: number;
  attemptCount: number;
};

function readResendMeta(): ResendMeta | null {
  try {
    const raw = localStorage.getItem(RESEND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResendMeta;
    if (
      typeof parsed.lastSentAt !== "number" ||
      typeof parsed.windowStartAt !== "number" ||
      typeof parsed.attemptCount !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeResendMeta(meta: ResendMeta) {
  localStorage.setItem(RESEND_STORAGE_KEY, JSON.stringify(meta));
}

function getResendState(now: number) {
  const meta = readResendMeta();
  if (!meta) {
    return {
      canSend: true,
      cooldownRemaining: 0,
      attemptsRemaining: RESEND_MAX_ATTEMPTS,
    };
  }

  const inWindow = now - meta.windowStartAt < RESEND_WINDOW_MS;
  const windowAttemptCount = inWindow ? meta.attemptCount : 0;
  const attemptsRemaining = Math.max(0, RESEND_MAX_ATTEMPTS - windowAttemptCount);
  const cooldownRemaining = Math.max(
    0,
    RESEND_COOLDOWN_SECONDS - Math.floor((now - meta.lastSentAt) / 1000),
  );

  return {
    canSend: attemptsRemaining > 0 && cooldownRemaining <= 0,
    cooldownRemaining,
    attemptsRemaining,
  };
}

function registerResendAttempt(now: number) {
  const previous = readResendMeta();
  const resetWindow = !previous || now - previous.windowStartAt >= RESEND_WINDOW_MS;
  const nextMeta: ResendMeta = {
    lastSentAt: now,
    windowStartAt: resetWindow ? now : previous.windowStartAt,
    attemptCount: resetWindow ? 1 : previous.attemptCount + 1,
  };
  writeResendMeta(nextMeta);
}

function App() {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading your session...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={session ? <AppLayout /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </Router>
  );
}

function AppLayout() {
  const { data: session } = authClient.useSession();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(RESEND_MAX_ATTEMPTS);

  const isEmailUnverified =
    !!session?.user?.email && session.user.emailVerified === false;

  React.useEffect(() => {
    const tick = () => {
      const state = getResendState(Date.now());
      setCooldownRemaining(state.cooldownRemaining);
      setAttemptsRemaining(state.attemptsRemaining);
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const handleResendVerification = async () => {
    if (!session?.user?.email) return;

    const state = getResendState(Date.now());
    if (!state.canSend) {
      if (state.attemptsRemaining <= 0) {
        setResendError("Resend limit reached. Please wait 15 minutes and try again.");
      } else {
        setResendError(`Please wait ${state.cooldownRemaining}s before resending.`);
      }
      return;
    }

    const loginURL = `${(process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, "")}/login`;
    setIsResending(true);
    setResendStatus(null);
    setResendError(null);

    try {
      const { error } = await authClient.sendVerificationEmail({
        email: session.user.email,
        callbackURL: `${loginURL}?verified=1`,
      });
      if (error) throw error;
      registerResendAttempt(Date.now());
      setResendStatus("Verification email sent. Check your inbox.");
    } catch (err: any) {
      const rawMessage = err?.message || "Could not resend verification email.";
      setResendError(
        /rate\s*limit|too\s*many|429/i.test(rawMessage)
          ? "Too many verification requests. Please wait a few minutes before trying again."
          : rawMessage,
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          window.location.href = "/login";
        },
      },
    });
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`bg-indigo-900 text-white transition-all duration-300 flex flex-col ${sidebarOpen ? "w-64" : "w-20"}`}
      >
        <div className="p-4 flex items-center justify-between">
          {sidebarOpen && <h1 className="text-xl font-bold">RetroRail</h1>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-indigo-800 rounded"
          >
            <Menu size={20} />
          </button>
        </div>
        <nav className="mt-8">
          <NavLink
            to="/"
            icon={<BarChart2 size={20} />}
            text="Dashboard"
            collapsed={!sidebarOpen}
          />
          <NavLink
            to="/sessions"
            icon={<Calendar size={20} />}
            text="Sessions"
            collapsed={!sidebarOpen}
          />
          <NavLink
            to="/reports"
            icon={<BarChart2 size={20} />}
            text="Reports"
            collapsed={!sidebarOpen}
          />
          <NavLink
            to="/billing"
            icon={<CreditCard size={20} />}
            text="Billing"
            collapsed={!sidebarOpen}
          />
          <NavLink
            to="/settings"
            icon={<SettingsIcon size={20} />}
            text="Settings"
            collapsed={!sidebarOpen}
          />
        </nav>
        <div className="mt-auto p-4">
          <button
            onClick={handleSignOut}
            className="w-full rounded-lg border border-indigo-700 px-3 py-2 text-sm hover:bg-indigo-800"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {isEmailUnverified && (
          <div className="mx-6 mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <p className="font-medium">Your email is not verified.</p>
            <p className="mt-1 text-sm">
              Please verify your email address to secure your account.
            </p>
            <button
              onClick={handleResendVerification}
              disabled={isResending || cooldownRemaining > 0 || attemptsRemaining <= 0}
              className="mt-3 text-sm font-medium underline disabled:opacity-60"
            >
              {isResending
                ? "Sending..."
                : attemptsRemaining <= 0
                  ? "Resend limit reached"
                  : cooldownRemaining > 0
                    ? `Resend in ${cooldownRemaining}s`
                    : "Resend verification email"}
            </button>
            <p className="mt-1 text-xs">Up to {RESEND_MAX_ATTEMPTS} resends every 15 minutes.</p>
            {resendStatus && <p className="mt-2 text-sm text-green-700">{resendStatus}</p>}
            {resendError && <p className="mt-2 text-sm text-red-700">{resendError}</p>}
          </div>
        )}
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/sessions/:id" element={<SessionDetail />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}

function NavLink({
  to,
  icon,
  text,
  collapsed,
}: {
  to: string;
  icon: React.ReactNode;
  text: string;
  collapsed: boolean;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 hover:bg-indigo-800 transition-colors"
    >
      {icon}
      {!collapsed && <span>{text}</span>}
    </Link>
  );
}

export default App;
